import React, { useState } from "react";
import { useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
/* Tab list and Employer tab commented out – single Candidate form only. To restore, uncomment: import { Tab, TabList, TabPanel, Tabs } from "react-tabs"; */
import { useAuth } from "../../contexts/AuthContext";
import { authAPI } from "../../services/api";
import VerificationEmailModal from "../VerificationEmailModal";
import "./style.scss";

function SignUp() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [showPass, setShowPass] = useState(false);
  const [showPass2, setShowPass2] = useState(false);
  const [showPass3, setShowPass3] = useState(false);
  const [showPass4, setShowPass4] = useState(false);
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState("");
  const [passwordErrors, setPasswordErrors] = useState({
    minLength: false,
    hasNumber: false,
    hasSpecialChar: false,
  });

  // Password validation
  const validatePassword = (password) => {
    const errors = {
      minLength: password.length >= 8,
      hasNumber: /\d/.test(password),
      hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    };
    setPasswordErrors(errors);
    return errors.minLength && errors.hasNumber && errors.hasSpecialChar;
  };

  const handlePasswordChange = (e) => {
    const password = e.target.value;
    setFormData({ ...formData, password });
    if (password.length > 0) {
      validatePassword(password);
    } else {
      setPasswordErrors({
        minLength: false,
        hasNumber: false,
        hasSpecialChar: false,
      });
    }
  };

  const handleSubmit = async (e, userType = "candidate") => {
    e.preventDefault();
    setError("");
    setLoading(true);

    // Basic validation
    if (!formData.fullName || !formData.email || !formData.password) {
      setError("Please fill in all required fields");
      setLoading(false);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      setLoading(false);
      return;
    }

    if (!validatePassword(formData.password)) {
      setError("Password does not meet requirements");
      setLoading(false);
      return;
    }

    try {
      // Call registration API
      const response = await authAPI.register({
        email: formData.email,
        password: formData.password,
        role: userType,
        fullName: formData.fullName,
      });

      if (response.data.success) {
        // Show verification modal instead of auto-login
        setRegisteredEmail(formData.email);
        setShowVerificationModal(true);

        // Clear form
        setFormData({
          fullName: "",
          email: "",
          password: "",
          confirmPassword: "",
        });
      } else {
        setError(response.data.message || "Registration failed");
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || "Registration failed. Please try again.";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {showVerificationModal && (
        <VerificationEmailModal
          email={registeredEmail}
          onClose={() => {
            setShowVerificationModal(false);
            navigate("/login");
          }}
          onResend={() => {
            // Modal will handle the resend logic
          }}
        />
      )}
      <section className="account-section">
        <div className="tf-container">
          <div className="row">
            <div className="wd-form-login tf-tab">
            <div className="register-header-text">
              <h4>Create your free Talsolution account</h4>
              <p>Apply to US jobs, track applications, and message recruiters.</p>
            </div>
            {/* Whole tab list and Employer tab commented out – single Candidate form only. To restore: use Tabs, TabList, Tab, TabPanel and uncomment the Employer block below. */}
            <div className="content-tab">
              {/* Use "inner" only – "animation-tab" is hidden by global CSS unless react-tabs adds --selected */}
              <div className="inner">
                {error && <div className="alert alert-danger">{error}</div>}
                <form onSubmit={(e) => handleSubmit(e, "candidate")}>
                  <div className="ip">
                    <label>
                      Full Name<span>*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.fullName}
                      onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                      placeholder="John Doe"
                      required
                    />
                  </div>
                  <div className="ip">
                    <label>
                      Email address<span>*</span>
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="your.email@example.com"
                      required
                    />
                  </div>
                  <div className="ip">
                    <label>
                      Password<span>*</span>
                    </label>
                    <div className="inputs-group auth-pass-inputgroup">
                      <input
                        type={showPass ? "text" : "password"}
                        className="input-form password-input"
                        value={formData.password}
                        onChange={handlePasswordChange}
                        placeholder="Create a strong password"
                        required
                      />
                      <span
                        className={`password-addon ${
                          showPass ? "icon-eye" : "icon-eye-off"
                        }`}
                        onClick={(e) => {
                          e.preventDefault();
                          setShowPass(!showPass);
                        }}
                      />
                    </div>
                    {formData.password.length > 0 && (
                      <div className="password-requirements">
                        <div className={`req-item ${passwordErrors.minLength ? "valid" : ""}`}>
                          <span className="req-icon">{passwordErrors.minLength ? "✓" : "○"}</span>
                          Minimum 8 characters
                        </div>
                        <div className={`req-item ${passwordErrors.hasNumber ? "valid" : ""}`}>
                          <span className="req-icon">{passwordErrors.hasNumber ? "✓" : "○"}</span>
                          At least 1 number
                        </div>
                        <div className={`req-item ${passwordErrors.hasSpecialChar ? "valid" : ""}`}>
                          <span className="req-icon">{passwordErrors.hasSpecialChar ? "✓" : "○"}</span>
                          At least 1 special character
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="ip">
                    <label>
                      Confirm Password<span>*</span>
                    </label>
                    <div className="inputs-group auth-pass-inputgroup">
                      <input
                        type={showPass2 ? "text" : "password"}
                        className="input-form password-input"
                        value={formData.confirmPassword}
                        onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                        placeholder="Confirm your password"
                        required
                      />
                      <span
                        className={`password-addon ${
                          showPass2 ? "icon-eye" : "icon-eye-off"
                        }`}
                        onClick={(e) => {
                          e.preventDefault();
                          setShowPass2(!showPass2);
                        }}
                      />
                    </div>
                  </div>
                  <div className="email-verification-notice">
                    <p>📧 We'll send a verification email to confirm your account.</p>
                    <p className="notice-text">Please verify your email to apply for jobs.</p>
                  </div>
                  <div className="group-ant-choice st">
                    <div className="sub-ip">
                      <input type="checkbox" required />I agree to the
                      <Link to="/termsofuse"> Terms of Service</Link> and
                      <Link to="/privacypolicy"> Privacy Policy</Link>
                    </div>
                  </div>
                  <button type="submit" disabled={loading}>
                    {loading ? "Creating Account..." : "Create Free Account"}
                  </button>
                  <div className="free-message">
                    <p>100% free for candidates. No fees. No salary cuts.</p>
                  </div>
                  <div className="sign-up">
                    Already have an account?
                    <Link to="/login">&nbsp;Login</Link>
                  </div>
                </form>
              </div>
              {/* Employer tab panel – to be implemented in the future */}
              {/* <TabPanel className="inner animation-tab">
                {error && <div className="alert alert-danger">{error}</div>}
                <form onSubmit={(e) => handleSubmit(e, "employer")}>
                  <div className="ip">
                    <label>
                      Full Name<span>*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.fullName}
                      onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                      placeholder="Your name"
                      required
                    />
                  </div>
                  <div className="ip">
                    <label>
                      Email address<span>*</span>
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="your.email@example.com"
                      required
                    />
                  </div>
                  <div className="ip">
                    <label>
                      Password<span>*</span>
                    </label>
                    <div className="inputs-group auth-pass-inputgroup">
                      <input
                        type={showPass3 ? "text" : "password"}
                        className="input-form password-input"
                        value={formData.password}
                        onChange={handlePasswordChange}
                        placeholder="Create a strong password"
                        required
                      />
                      <span
                        className={`password-addon ${
                          showPass3 ? "icon-eye" : "icon-eye-off"
                        }`}
                        onClick={(e) => {
                          e.preventDefault();
                          setShowPass3(!showPass3);
                        }}
                      />
                    </div>
                    {formData.password.length > 0 && (
                      <div className="password-requirements">
                        <div className={`req-item ${passwordErrors.minLength ? "valid" : ""}`}>
                          <span className="req-icon">{passwordErrors.minLength ? "✓" : "○"}</span>
                          Minimum 8 characters
                        </div>
                        <div className={`req-item ${passwordErrors.hasNumber ? "valid" : ""}`}>
                          <span className="req-icon">{passwordErrors.hasNumber ? "✓" : "○"}</span>
                          At least 1 number
                        </div>
                        <div className={`req-item ${passwordErrors.hasSpecialChar ? "valid" : ""}`}>
                          <span className="req-icon">{passwordErrors.hasSpecialChar ? "✓" : "○"}</span>
                          At least 1 special character
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="ip">
                    <label>
                      Confirm Password<span>*</span>
                    </label>
                    <div className="inputs-group auth-pass-inputgroup">
                      <input
                        type={showPass4 ? "text" : "password"}
                        className="input-form password-input"
                        value={formData.confirmPassword}
                        onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                        placeholder="Confirm your password"
                        required
                      />
                      <span
                        className={`password-addon ${
                          showPass4 ? "icon-eye" : "icon-eye-off"
                        }`}
                        onClick={(e) => {
                          e.preventDefault();
                          setShowPass4(!showPass4);
                        }}
                      />
                    </div>
                  </div>
                  <div className="email-verification-notice">
                    <p>📧 We'll send a verification email to confirm your account.</p>
                  </div>
                  <div className="group-ant-choice st">
                    <div className="sub-ip">
                      <input type="checkbox" required />I agree to the
                      <Link to="/termsofuse"> Terms of Service</Link> and
                      <Link to="/privacypolicy"> Privacy Policy</Link>
                    </div>
                  </div>
                  <button type="submit" disabled={loading}>
                    {loading ? "Creating Account..." : "Create Free Account"}
                  </button>
                  <div className="sign-up">
                    Already have an account?
                    <Link to="/login">&nbsp;Login</Link>
                  </div>
                </form>
              </TabPanel> */}
            </div>
          </div>
        </div>
      </div>
    </section>
    </>
  );
}

export default SignUp;
